import React from 'react';
import { StyleSheet, View } from 'react-native';
import { colors, screenPadding, spacing } from '../../theme';
import BottomSheet from './BottomSheet';
import Button from './Button';
import Icon, { IconName } from './Icon';
import Text from './Text';

/**
 * "Are you sure?", once, properly.
 *
 * Four screens needed this - taking a photo down, cancelling a booking that
 * cannot be un-cancelled, calling off an alarm, deleting a review - and four
 * hand-rolled versions is four chances for the destructive action to end up on
 * the left, or styled like the safe one, or missing its explanation.
 *
 * ---------------------------------------------------------------------------
 * THE RULES IT ENFORCES
 * ---------------------------------------------------------------------------
 *   - The confirm button says what it does ("Remove photo"), never "Yes". A
 *     button labelled "Yes" is only readable with the question still in view,
 *     which it will not be on a small screen with a keyboard up.
 *   - The safe way out is the wider, calmer control and it is listed second,
 *     under the thumb - dismissing is the more common intent by a long way.
 *   - `tone="danger"` colours only the confirm button. Painting the whole
 *     sheet red makes a routine cancellation feel like an error state.
 */

type Props = {
  visible: boolean;
  title: string;
  /** What actually happens, in a sentence. Never optional in practice. */
  body: string;
  /** What the destructive button says - a verb, not "Yes". */
  confirmLabel: string;
  /**
   * The way out, for a sheet that asks something.
   *
   * Pass `null` for a sheet that only REPORTS - a failure, a receipt - where
   * there is nothing to decline and both buttons would dismiss identically.
   * Two buttons doing the same thing make someone stop and work out the
   * difference before pressing either.
   */
  cancelLabel?: string | null;
  /** Sits above the title. Omit for a neutral confirmation. */
  icon?: IconName;
  tone?: 'danger' | 'neutral';
  onConfirm: () => void;
  onClose: () => void;
  /** Extra copy - a fee, a consequence - between body and buttons. */
  children?: React.ReactNode;
};

function ConfirmSheet({
  visible,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Keep it',
  icon,
  tone = 'danger',
  onConfirm,
  onClose,
  children,
}: Props) {
  const danger = tone === 'danger';

  return (
    <BottomSheet visible={visible} onClose={onClose}>
      <View style={styles.body}>
        {icon ? (
          <View style={[styles.mark, danger ? styles.markDanger : null]}>
            <Icon
              name={icon}
              size={22}
              color={danger ? colors.danger : colors.primary}
            />
          </View>
        ) : null}

        <Text variant="title" align="center">
          {title}
        </Text>
        <Text variant="bodySm" align="center" color={colors.inkBody}>
          {body}
        </Text>

        {children}

        <View style={styles.actions}>
          <Button
            label={confirmLabel}
            variant={danger ? 'danger' : 'primary'}
            onPress={onConfirm}
          />
          {/* Omitted entirely when there is no second choice to offer. */}
          {cancelLabel === null ? null : (
            <Button label={cancelLabel} variant="secondary" onPress={onClose} />
          )}
        </View>
      </View>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  body: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.lg,
  },
  mark: {
    width: 46,
    height: 46,
    borderRadius: 23,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
    marginBottom: spacing.xs,
  },
  markDanger: {
    backgroundColor: colors.dangerSurface,
  },
  actions: {
    alignSelf: 'stretch',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
});

export default ConfirmSheet;
